/**
 * Pure resolution of `WorkbenchPlugin` slots against an explicit plugin list (20a). The workbench registry delegates to
 * these with its active plugins; the agent view harness (`@agentic/ui-kit/testing`) uses them so pack tests resolve
 * slots exactly as the host does. A contributor that throws on unexpected data contributes nothing.
 */
import { isTerminalState, type TaskView } from '@agentic/contracts';
import type { ComponentType } from 'react';
import type {
  CasePanel,
  CasePanelContext,
  HostShows,
  NextAction,
  NextActionContext,
  PluginRoute,
  ProposalRendererProps,
  SummaryFact,
  TaskDetailContext,
  TaskDetailContribution,
  TaskPanel,
  WorkbenchPlugin,
} from './plugin.ts';

/** The proposal renderer for a capability, if a plugin registered one (first plugin wins). */
export function proposalRendererFor(
  capability: string,
  list: readonly WorkbenchPlugin[],
): ComponentType<ProposalRendererProps> | undefined {
  for (const p of list) {
    const r = p.proposalRenderers?.[capability];
    if (r) return r;
  }
  return undefined;
}

/**
 * Material figures for the case overview from the plugin that handles the task's capability; `[]` when no plugin
 * provides them (the generic overview shows none) or the provider throws on unexpected data.
 */
export function summaryFactsFor(view: TaskView, list: readonly WorkbenchPlugin[]): SummaryFact[] {
  for (const p of list) {
    const fn = p.summaryFacts?.[view.task.capability];
    if (!fn) continue;
    try {
      return fn(view);
    } catch {
      return [];
    }
  }
  return [];
}

/**
 * Task detail framework contribution (workstream 14) of the plugin that handles the task's capability: only the parts
 * the plugin defines (undefined parts are dropped, so the host shows them as Unknown). `{}` when no plugin contributes
 * or the contributor throws on unexpected data.
 */
export function taskDetailFor(
  view: TaskView,
  ctx: TaskDetailContext,
  list: readonly WorkbenchPlugin[],
): Partial<TaskDetailContribution> {
  for (const p of list) {
    const fn = p.taskDetail?.[view.task.capability];
    if (!fn) continue;
    let extra: Partial<TaskDetailContribution>;
    try {
      extra = fn(view, ctx);
    } catch {
      return {};
    }
    return Object.fromEntries(
      Object.entries(extra).filter(([, v]) => v !== undefined),
    ) as Partial<TaskDetailContribution>;
  }
  return {};
}

/** Extra panels of the agent activity view for a task; values are `<plugin id>:<panel id>`. */
export function taskPanelsFor(
  view: TaskView,
  list: readonly WorkbenchPlugin[],
): (TaskPanel & { value: string })[] {
  return list.flatMap((p) =>
    (p.taskPanels?.(view) ?? []).map((panel) => ({ ...panel, value: `${p.id}:${panel.id}` })),
  );
}

/**
 * Pack panels of the generic case page (17e) for the case's process type; values are `<plugin id>:<panel id>`. A
 * contributor that throws on unexpected data contributes nothing (the generic shell still renders).
 */
export function casePanelsFor(
  processType: string,
  ctx: CasePanelContext,
  list: readonly WorkbenchPlugin[],
): (CasePanel & { value: string })[] {
  return list.flatMap((p) => {
    const fn = p.casePanels?.[processType];
    if (!fn) return [];
    try {
      return fn(ctx).map((panel) => ({ ...panel, value: `${p.id}:${panel.id}` }));
    } catch {
      return [];
    }
  });
}

/** Plugin pages mounted at `/x/<plugin id>/<path>`. */
export function pluginRoutes(
  list: readonly WorkbenchPlugin[],
): (PluginRoute & { pluginId: string; href: string })[] {
  return list.flatMap((p) =>
    (p.routes ?? []).map((r) => ({ ...r, pluginId: p.id, href: `/x/${p.id}/${r.path}`.replace(/\/$/, '') })),
  );
}

/** A pack renderer of an approval request subject (`WorkbenchPlugin.approvalSubjects` value). */
export type ApprovalSubjectRenderer = NonNullable<WorkbenchPlugin['approvalSubjects']>[string];

/**
 * The renderer a plugin registered for an approval request subject type (`approvalSubjects`), if any (17e). The
 * approval request page shows the subject reference, revision and hash without one.
 */
export function approvalSubjectFor(
  subjectType: string,
  list: readonly WorkbenchPlugin[],
): ApprovalSubjectRenderer | undefined {
  for (const p of list) {
    const r = p.approvalSubjects?.[subjectType];
    if (r) return r;
  }
  return undefined;
}

/**
 * The pack's edit of a next action for the task's capability, applied to `base` (the host default); `undefined` when no
 * plugin edits it or the editor throws. Raw pack output: the workbench's `nextActionFor` additionally caps consequences
 * and drops stop links of ended tasks.
 */
export function packNextActionFor(
  view: TaskView,
  ctx: NextActionContext,
  base: NextAction,
  list: readonly WorkbenchPlugin[],
): NextAction | undefined {
  for (const p of list) {
    const fn = p.nextAction?.[view.task.capability];
    if (!fn) continue;
    try {
      return fn(view, ctx, { ...base, consequences: [...base.consequences] });
    } catch {
      return undefined;
    }
  }
  return undefined;
}

/** Consequence lines the host shows at most (the follow-up copy has five; the contract asks for two to four). */
export const MAX_CONSEQUENCES = 5;

/**
 * The next action the host shows (one path for the workbench and the `mountAgentView` harness): the pack's edit of
 * `base` when a plugin edits it for the capability (a throwing plugin falls back to `base`), with consequences capped
 * at `MAX_CONSEQUENCES` and no stop / cancel links once the episode has ended.
 */
export function resolveNextAction(
  view: TaskView,
  ctx: NextActionContext,
  base: NextAction,
  list: readonly WorkbenchPlugin[],
): NextAction {
  const action = packNextActionFor(view, ctx, base, list) ?? base;
  const secondary = isTerminalState(view.task.state) ? undefined : action.secondary;
  const { secondary: _drop, ...rest } = action;
  return {
    ...rest,
    consequences: action.consequences.slice(0, MAX_CONSEQUENCES),
    ...(secondary?.length ? { secondary } : {}),
  };
}

/**
 * Blocks the case page's episode view shows outside a pack's proposal renderer (summary, attention list, figure list,
 * actions table), passed to renderers so they do not repeat them.
 */
export function caseHostShows(factCount: number): HostShows {
  return { summary: true, facts: factCount > 0, warnings: true, actionStatus: true };
}
